/**
 * The contracts for the seven screens other agents own.
 *
 * This file exists so those screens can be built against a fixed shape and
 * dropped into `App.tsx` with a one-line change each. Nothing in here is
 * rendered; it is types plus the one constant a screen needs.
 *
 * ## What every screen must satisfy
 *
 * 1. It is the **default export** of its own file in `src/pages/`, and its
 *    props type is `ScreenProps` - that is, it takes none. Everything a screen
 *    needs comes from `useSession()`, from `useParams()`, or from the query
 *    hooks in `lib/queries.ts`.
 * 2. It renders its own `<PageHeader>` and nothing else above it. `AppShell`
 *    is already wrapped around it by the router.
 * 3. It renders a loading state, an empty state and an error state. Use
 *    `LoadingScreen`, `EmptyState` and `ErrorState` - the error copy is
 *    derived from the thrown error in one place and should not be rewritten.
 * 4. It never declares a DTO. Every response shape comes from
 *    `@botguide/shared`.
 * 5. It uses `useCan(action, kind)` to decide whether to render a control, and
 *    it understands that this is cosmetic - see the header of `session.tsx`.
 *
 * ## The seven
 *
 * | File                    | Route            | Roles                | Reads |
 * |-------------------------|------------------|----------------------|-------|
 * | `pages/WorkBoard.tsx`   | `/board`         | owner, contractor    | `useBoard`, `useMoveTask` |
 * | `pages/WorkItems.tsx`   | `/work`          | owner                | `useClients`, `useApplications`, `useProjects`, `useFeatures` |
 * | `pages/People.tsx`      | `/people`        | owner, contractor    | `useUsers`, `useCreateUser`, `useUpdateUser` |
 * | `pages/Capacity.tsx`    | `/capacity`      | owner, contractor    | `useCapacity` |
 * | `pages/MyHours.tsx`     | `/hours`         | owner, contractor    | `useTimeEntries`, `useCreateTimeEntry` |
 * | `pages/HoursReview.tsx` | `/hours/review`  | owner                | `useTimeEntries`, `useReviewTimeEntry` |
 * | `pages/ClientDashboard.tsx`  | `/dashboard`, `/dashboard/:clientId` | client, owner | `useClientDashboard` |
 */
import type { ComponentType } from 'react';

/** Screens take no props. State comes from the session, the URL and queries. */
export type ScreenProps = Record<string, never>;

/** The shape `App.tsx` imports. `const Screen: ScreenComponent = ...`. */
export type ScreenComponent = ComponentType<ScreenProps>;

/**
 * `/dashboard/:clientId` is an owner previewing a client's view. `ClientDashboard`
 * reads it with `useParams<ClientDashboardParams>()`; when it is absent the
 * API resolves the caller's own client instead.
 */
export interface ClientDashboardParams {
  clientId?: string;
  [key: string]: string | undefined;
}

/**
 * Every screen owns a board-style filter set in the query string rather than
 * in component state, so a filtered view can be linked to and survives a
 * reload. `boardQuerySchema` in `@botguide/shared` is the parser.
 */
export const BOARD_FILTER_PARAMS = [
  'clientId',
  'applicationId',
  'projectId',
  'assigneeId',
  'plannedWeek',
  'blockedOnly',
] as const;
